import { FC, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  FormHelperText,
  Grid,
  InputAdornment,
  Typography
} from '@material-ui/core';
import IconButton from '@material-ui/core/Icon';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import i18n from 'i18next';
import { LoginContainer } from 'src/components/loginContainer';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { InputField } from '../../components/input-field';
import { useMounted } from '../../hooks/use-mounted';
import { LockClosed } from '../../icons/lock-closed';
import VisibilityIcon from '../../icons/visibilityIcon';
import VisibilityIconOff from '../../icons/visibilityIconOff';
import { updatePasswordConfirmation, selectAuth } from './authSlice';

const features = [
  'Le mot de passe doit:',
  'avoir au moins 8 caractères',
  'inclure les éléments suivants :',
  ' - un caractère majuscule (A-Z)',
  ' - un caractère minuscule (a-z)',
  ' - un numéro (1-9)',
  ' - un caractère spécial (&”@)'
];
const ResetPasswordPage: FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const mounted = useMounted();
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(selectAuth);
  const noPointer = { cursor: 'pointer' };
  const passwordRegex = RegExp(
    "^(?=.*[A-Z])(?=.*[a-zA-Z0-9À-ÿ])(?=.*[@$!%*?&+\\-.,;=^\"`'#_{}()§£/:|\\[\\]])[a-zA-Z0-9À-ÿ@$!%*?&+\\-.,;=^\"`'#_{}()§£/:|\\[\\]]{8,}$"
  );

  const [showpass, setShowPass] = useState({
    pass: false,
    passConfirm: false
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmationPassword: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .required('Mot de pass obligaoire')
        .matches(passwordRegex, 'Mot de passe invalide'),
      confirmationPassword: Yup.string().oneOf(
        [Yup.ref('password'), null],
        'Mot de passe non identique'
      )
    }),
    onSubmit: async (values, helpers) => {
      try {
        const result = await dispatch(
          updatePasswordConfirmation({
            pas: values.password,
            confirmedPas: values.confirmationPassword,
            token
          })
        );
        if (updatePasswordConfirmation.fulfilled.match(result)) {
          toast.success('Votre mot de passe a été modifié avec succès.');
          helpers.setStatus({ success: true });

          navigate('/login');
        } else {
          helpers.setStatus({ success: false });
        }
        if (mounted.current) {
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        }
      } catch (err) {
        console.error(err);
        if (mounted.current) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    }
  });

  const handleClickShowPass = () => {
    setShowPass({
      ...showpass,
      pass: !showpass.pass
    });
  };
  const handleClickShowPassConfirm = () => {
    setShowPass({
      ...showpass,
      passConfirm: !showpass.passConfirm
    });
  };

  return (
    <LoginContainer>
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            mb: 3
          }}
        >
          <Typography color="textPrimary" variant="h4">
            Mot de passe oublié
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputField
              error={Boolean(formik.touched.password && formik.errors.password)}
              fullWidth
              helperText={formik.touched.password && formik.errors.password}
              name="password"
              placeholder="Créer votre mot de passe"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type={showpass.pass ? 'text' : 'password'}
              value={formik.values.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockClosed />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    style={{ marginRight: '0.5vh', marginTop: '0.5vh' }}
                  >
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPass}
                      style={noPointer}
                    >
                      {showpass.pass ? (
                        <VisibilityIcon style={noPointer} />
                      ) : (
                        <VisibilityIconOff style={noPointer} />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <InputField
              error={Boolean(
                formik.touched.confirmationPassword && formik.errors.confirmationPassword
              )}
              fullWidth
              helperText={formik.touched.confirmationPassword && formik.errors.confirmationPassword}
              name="confirmationPassword"
              placeholder="Confirmer votre mot de passe"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type={showpass.passConfirm ? 'text' : 'password'}
              value={formik.values.confirmationPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockClosed />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    style={{ marginRight: '0.5vh', marginTop: '0.5vh' }}
                  >
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassConfirm}
                      style={noPointer}
                    >
                      {showpass.passConfirm ? (
                        <VisibilityIcon style={noPointer} />
                      ) : (
                        <VisibilityIconOff style={noPointer} />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          {formik.errors.submit && (
            <Grid item xs={12}>
              <FormHelperText error>{formik.errors.submit as React.ReactNode}</FormHelperText>
            </Grid>
          )}
          <Grid item xs>
            {features.map((feature) => (
              <Grid key={feature} fontSize={12}>
                {feature}
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <Button
              color="primary"
              disabled={formik.isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              Créer
            </Button>
          </Grid>
        </Grid>
        {status === 'error' && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {i18n.t('auth.errors.update_Pass')}
          </Alert>
        )}
      </form>
    </LoginContainer>
  );
};
export default ResetPasswordPage;
